import { useState } from "preact/hooks"
import { Table, type TableColumn, type TableSort } from "@/modules/Table"
import { Toggle } from "@/modules/Toggle"
import { useQueryState } from "@/modules/useStateQuery"
import classes from "./index.module.scss"

type Config = {
	selectedFilters: { [filterKey: string]: string[] }
	sort: TableSort
}

export type Filter<T> = {
	label: string
	options: { [key: string]: string }
	defaults: string[]
	filter: (row: T, selectedValues: string[]) => boolean
}

export declare type TableProps<T> = {
	data: T[]
	defaultSort: TableSort
	filters: { [key: string]: Filter<T> }
	columns: TableColumn<T>[]
}

function buildDefaultFilters<T>(filters: { [key: string]: Filter<T> }): {
	[filterKey: string]: string[]
} {
	const selectedFilters: { [filterKey: string]: string[] } = {}
	for (const filterKey in filters) {
		const filter = filters[filterKey]
		selectedFilters[filterKey] = filter.defaults
	}
	return selectedFilters
}

function InternalDataTable<T>(props: TableProps<T>) {
	const [showFilters, setShowFilters] = useState(false)

	const [config, setConfig] = useQueryState<Config>("config", {
		selectedFilters: buildDefaultFilters(props.filters),
		sort: props.defaultSort,
	})

	const setSort = (sort: TableSort) => {
		const newValue = JSON.parse(JSON.stringify(config)) as Config
		newValue.sort = sort
		setConfig(newValue)
	}

	const toggleFilterOptions = (filterKey: string, optionKey: string) => {
		const newValue = JSON.parse(JSON.stringify(config)) as Config
		if (config.selectedFilters[filterKey].includes(optionKey)) {
			newValue.selectedFilters[filterKey] = config.selectedFilters[
				filterKey
			].filter((e) => e !== optionKey)
		} else {
			newValue.selectedFilters[filterKey].push(optionKey)
		}

		setConfig(newValue)
	}

	return (
		<div className={classes.main}>
			<div className={classes.filterContainer}>
				<Toggle label="Filters" setValue={setShowFilters} value={showFilters} />
				<div
					className={classes.filterBox}
					style={{ display: showFilters ? "flex" : "none" }}
				>
					<div className={classes.filters}>
						{Object.keys(props.filters).map((filterKey) => {
							const filter = props.filters[filterKey]

							return (
								<div className={classes.filter} key={filterKey}>
									<h2>{filter.label}</h2>
									<div className={classes.links}>
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault()
												const newValue = JSON.parse(
													JSON.stringify(config),
												) as Config
												newValue.selectedFilters[filterKey] = Object.values(
													props.filters[filterKey].options,
												)
												setConfig(newValue)
											}}
										>
											Select All
										</a>
										<a
											href="#"
											onClick={(e) => {
												e.preventDefault()
												const newValue = JSON.parse(
													JSON.stringify(config),
												) as Config
												newValue.selectedFilters[filterKey] = []
												setConfig(newValue)
											}}
										>
											Select None
										</a>
									</div>
									{Object.keys(filter.options).map((optionKey) => {
										const option = filter.options[optionKey]
										return (
											<label key={`${filterKey}-${optionKey}`}>
												<input
													checked={config.selectedFilters[filterKey].includes(
														option,
													)}
													onChange={() =>
														toggleFilterOptions(filterKey, option)
													}
													type="checkbox"
												/>
												{option}
											</label>
										)
									})}
								</div>
							)
						})}
					</div>
					<div
						className={classes.buttons}
						onClick={(e) => {
							e.preventDefault()
							const newValue = JSON.parse(JSON.stringify(config)) as Config
							newValue.selectedFilters = buildDefaultFilters(props.filters)
							setConfig(newValue)
						}}
					>
						<button>Reset Filters</button>
					</div>
				</div>
			</div>

			<Table
				columns={props.columns}
				data={props.data.filter((row) => {
					for (const filterKey of Object.keys(config.selectedFilters)) {
						if (
							!props.filters[filterKey].filter(
								row,
								config.selectedFilters[filterKey],
							)
						) {
							return false
						}
					}

					return true
				})}
				setSort={setSort}
				sort={config.sort}
			/>
		</div>
	)
}

export function DataTable<T>(props: TableProps<T>) {
	return <InternalDataTable {...props} />
}
